import React, { useRef, useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Car, RefreshCw, Wrench, Phone, CheckCircle, Shield, Clock, Award, Users } from 'lucide-react';
import { mockServices, mockCompanyInfo } from '../mock';

const Services = () => {
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(true); // Start visible

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => setIsVisible(entry.isIntersecting),
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const serviceIcons = { car: Car, refresh: RefreshCw, wrench: Wrench };

  const handleContactService = (service) => {
    const message = `שלום, אני מעוניין/ת בשירות: ${service.title}. אשמח לקבל פרטים נוספים.`;
    const phoneNumber = mockCompanyInfo.whatsapp.replace(/[^0-9]/g, '');
    window.open(`https://wa.me/972${phoneNumber.substring(1)}?text=${encodeURIComponent(message)}`, '_blank');
  };

  const whyChooseUs = [
    { icon: Shield, title: 'אמינות מוכחת', desc: 'שנים של ניסיון ואלפי לקוחות מרוצים' },
    { icon: Clock, title: 'שירות מהיר', desc: 'תהליכים יעילים וחסכון בזמן יקר' },
    { icon: Award, title: 'איכות ללא פשרות', desc: 'רק רכבים שעוברים בדיקה קפדנית' },
    { icon: Users, title: 'ליווי אישי', desc: 'צוות מקצועי שמלווה אותך בכל שלב' }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.15 } }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
  };

  return (
    <section id="services" ref={sectionRef} className="py-24 bg-luxury-black relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-luxury-gold/3 rounded-full blur-3xl" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 relative z-10">
        {/* Header */}
        <motion.div 
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          animate={isVisible ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
        >
          <div className="flex items-center justify-center gap-3 mb-6">
            <div className="h-px w-12 bg-luxury-gold" />
            <span className="text-luxury-gold text-sm font-medium tracking-[0.3em] uppercase">מה אנחנו מציעים</span>
            <div className="h-px w-12 bg-luxury-gold" />
          </div>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-luxury-text mb-6">
            השירותים <span className="gold-shimmer">שלנו</span>
          </h2>
          <p className="text-lg text-luxury-muted max-w-2xl mx-auto">
            מקצועיות ואמינות בכל שירות - מרכישה ועד טיפוח מתקדם
          </p>
        </motion.div>

        {/* Services Grid */}
        <motion.div 
          className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8 mb-20"
          variants={containerVariants}
          initial="hidden"
          animate={isVisible ? "visible" : "hidden"}
        >
          {mockServices.map((service, index) => {
            const IconComponent = serviceIcons[service.icon];
            
            return (
              <motion.div key={service.id} variants={itemVariants}>
                <Card className="h-full bg-luxury-surface border-luxury-border hover:border-luxury-gold/50 transition-all duration-500 group overflow-hidden relative">
                  {/* Gold accent line */}
                  <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-luxury-gold to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  
                  <CardHeader className="text-center pb-4">
                    <motion.div 
                      className="w-16 h-16 bg-luxury-gold flex items-center justify-center mx-auto mb-6"
                      whileHover={{ rotate: 12, scale: 1.1 }}
                      transition={{ duration: 0.3 }}
                    >
                      <IconComponent className="w-8 h-8 text-luxury-black" />
                    </motion.div>
                    
                    <CardTitle className="text-2xl font-bold text-luxury-text mb-3 group-hover:text-luxury-gold transition-colors duration-300">
                      {service.title}
                    </CardTitle>
                    
                    <p className="text-luxury-muted leading-relaxed">
                      {service.description}
                    </p>
                  </CardHeader>

                  <CardContent className="space-y-6">
                    {/* Features */}
                    <div className="space-y-3">
                      {service.features.map((feature, idx) => (
                        <div key={idx} className="flex items-center gap-3">
                          <CheckCircle className="w-4 h-4 text-luxury-gold flex-shrink-0" />
                          <span className="text-luxury-muted text-sm">{feature}</span>
                        </div>
                      ))}
                    </div>

                    {/* CTA */}
                    <motion.button
                      onClick={() => handleContactService(service)}
                      className="btn-luxury w-full flex items-center justify-center gap-2"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <Phone className="w-4 h-4" />
                      <span>קבל הצעת מחיר</span>
                    </motion.button>
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </motion.div>

        {/* Why Choose Us */}
        <motion.div 
          className="glass border border-luxury-border p-8 md:p-12"
          initial={{ opacity: 0, y: 40 }}
          animate={isVisible ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.4 }}
        >
          <div className="text-center mb-12">
            <h3 className="text-3xl md:text-4xl font-bold text-luxury-text mb-4">
              למה לבחור ב<span className="text-luxury-gold">J CLASS MOTORS</span>?
            </h3>
            <p className="text-luxury-muted">
              הסיבות המובילות שגורמות ללקוחות לבחור בנו
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {whyChooseUs.map((item, index) => (
              <motion.div 
                key={index}
                className="text-center group"
                initial={{ opacity: 0, y: 20 }}
                animate={isVisible ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: 0.5 + index * 0.1 }}
              >
                <motion.div 
                  className="w-14 h-14 border border-luxury-gold/50 flex items-center justify-center mx-auto mb-4 group-hover:bg-luxury-gold group-hover:border-luxury-gold transition-all duration-300"
                  whileHover={{ scale: 1.1 }}
                >
                  <item.icon className="w-6 h-6 text-luxury-gold group-hover:text-luxury-black transition-colors duration-300" />
                </motion.div>
                <h4 className="text-lg font-bold text-luxury-text mb-2">{item.title}</h4>
                <p className="text-luxury-muted text-sm">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Services;
